/**
 * AgentResponse 또는 Agent에서 AvatarConfig를 생성하는 유틸리티
 *
 * 새로운 스키마 (2024.12 업데이트):
 * - agent_id는 data 레벨에 위치 (avatar 객체 바깥)
 * - avatar 객체에는 avatar_id가 별도로 존재
 */

import type { Agent, AgentResponse, AvatarAgentVoiceResponse } from '@/entities/agent/types/types';
import type { AvatarConfig } from '../types/websocket-messages';
import type { AvatarType } from '@/entities/avatar-scenario/const/config';
import { getAvatarAgentConfig } from '../const/config';
import { applyVoiceOverrides } from './voice-overrides';

// 기본 voice_id (voices 배열이 없을 때 사용)
// Voice Server에서 사용하는 기본 UUID 형식 voice_id
const DEFAULT_VOICE_ID = '550e8400-e29b-41d4-a716-446655440001';  // b2g-frontend 기본값

// 유효한 아바타 타입 목록
const VALID_AVATAR_TYPES: AvatarType[] = ['glyx', 'nalnani', 'kikiz', 'bambam', 'vroid', 'goesan', 'bobbi', 'taekwondo'];

/**
 * voices 배열에서 특정 언어의 voice_id를 찾습니다.
 */
function getVoiceIdByLanguage(
  voices: AvatarAgentVoiceResponse[] | undefined,
  languageCode: string
): string {
  if (!voices || voices.length === 0) return '';
  const voice = voices.find((v) => v.language_code === languageCode);
  return voice?.voice_id || '';
}

/**
 * buildAvatarConfigFromAgent 반환 타입
 */
export interface AvatarConfigWithAgentId {
  agentId: string;        // Agent UUID (data 레벨에 전달)
  avatarConfig: AvatarConfig;  // avatar 객체 내부
}

/**
 * Agent 또는 AgentResponse 데이터에서 AvatarConfig를 생성합니다.
 *
 * 새로운 스키마:
 * {
 *   "type": "avatar_setup",
 *   "data": {
 *     "session_id": "...",
 *     "agent_id": "UUID",        <- agentId
 *     "avatar": {                <- avatarConfig
 *       "avatar_name": "GLYX",
 *       "avatar_id": "glyx-1",
 *       "korean_voice_id": "...",
 *       ...
 *     }
 *   }
 * }
 *
 * @param agentData - useAgent 또는 useAgentByUrl에서 가져온 데이터
 * @returns agentId와 avatarConfig를 분리하여 반환
 */
export function buildAvatarConfigFromAgent(agentData: Agent | AgentResponse): AvatarConfigWithAgentId {
  // agent.config.avatar_type에서 아바타 타입 추출
  // Agent와 AgentResponse 모두에서 config를 안전하게 접근
  const agentConfig = 'config' in agentData ? agentData.config : undefined;
  const rawAvatarType = agentConfig?.avatar_type as AvatarType | undefined;
  const avatarType: AvatarType = rawAvatarType && VALID_AVATAR_TYPES.includes(rawAvatarType)
    ? rawAvatarType
    : 'nalnani'; // 기본값

  console.log('🎭 [AVATAR-CONFIG] 아바타 타입:', avatarType);

  // 해당 아바타의 기본 설정 가져오기
  const baseConfig = getAvatarAgentConfig(avatarType);

  // AgentResponse는 voices를 가지고, Agent는 voices가 없음
  const voices = 'voices' in agentData ? agentData.voices : undefined;

  // voices 배열이 있으면 해당 voice_id 사용, 없으면 아바타 기본값 사용
  const koreanVoiceId = getVoiceIdByLanguage(voices, 'ko') || baseConfig.korean_voice_id || DEFAULT_VOICE_ID;
  const englishVoiceId = getVoiceIdByLanguage(voices, 'en') || baseConfig.english_voice_id || koreanVoiceId;
  const chineseVoiceId = getVoiceIdByLanguage(voices, 'zh') || baseConfig.chinese_voice_id || koreanVoiceId;
  const japaneseVoiceId = getVoiceIdByLanguage(voices, 'ja') || baseConfig.japanese_voice_id || koreanVoiceId;

  // agent.config.voices(언어별 목소리 설정) 가 있으면 voices[]/프리셋보다 우선
  const avatarConfig: AvatarConfig = applyVoiceOverrides({
    avatar_name: baseConfig.avatar_name,
    avatar_id: baseConfig.avatar_id,  // avatar 객체 내부의 ID
    korean_voice_id: koreanVoiceId,
    english_voice_id: englishVoiceId,
    chinese_voice_id: chineseVoiceId,
    japanese_voice_id: japaneseVoiceId,
    avatar_type: baseConfig.avatar_type,
  }, agentConfig?.voices);

  console.log('🎭 [AVATAR-CONFIG] 생성된 설정:', { agentId: agentData.id, avatarConfig });

  return {
    agentId: agentData.id,  // Agent UUID (data 레벨)
    avatarConfig,           // avatar 객체
  };
}

/**
 * AvatarConfig가 유효한지 검증합니다.
 * 최소한 avatar_id, avatar_name, korean_voice_id가 있어야 유효합니다.
 */
export function isValidAvatarConfig(config: AvatarConfig | null): config is AvatarConfig {
  if (!config) return false;
  return Boolean(config.avatar_id && config.avatar_name && config.korean_voice_id);
}
